import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject, Injector, signal } from '@angular/core'
import { applyEach, FieldTree, form, required, validate } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { KnowYourCustomer } from '@lemon/core-firebase/model/kayttaja'
import { KycUriService } from '@lemon/core-firebase/service/kyc-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { VeroilmoitusDraft, VeroilmoitusOsakas, VeroilmoitusOsakkaatHistoria } from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { VeroilmoitusLaskentaService } from 'app/_jaettu-lemonator/service/veroilmoitus/veroilmoitus-laskenta.service'
import { Observable, Subject, Subscription, firstValueFrom, map, of as observableOf, switchMap, takeUntil, tap } from 'rxjs'
import { VeroilmoituksenMuokkaustiedot } from '../veroilmoitus.component'

interface OsakasLomake {
  nimi: string
  hetu: string
  osakkeita: number | null
}

interface OsakkaatLomake {
  osakkaat: OsakasLomake[]
}

@Component({
  selector: '[app-kirjanpito-veroilmoitus-osakkaat]',
  templateUrl: './veroilmoitus-osakkaat.component.html',
  styleUrls: ['./veroilmoitus-osakkaat.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitoVeroilmoitusOsakkaatComponent implements OnInit, OnDestroy {
  @Input() veroilmoitusObservable: Observable<VeroilmoituksenMuokkaustiedot>
  @Input() showSnackbar?: boolean

  namename = 'xasd' + Math.random()

  readonly isDataFromKyc = signal(false)
  commonError: string

  readonly tuntemistiedotTallennettu = signal('')

  private _cache: VeroilmoitusOsakas[] = []
  private _rivienTilaukset: Subscription[] = []
  private readonly _injector = inject(Injector)

  lomake = form(signal<OsakkaatLomake>({ osakkaat: [] }), polku => {
    // FormValidators.vahintaanYksiArrayssaValidator tunnistaa vain FormArrayn, joten sama ehto on kirjoitettu tähän
    validate(polku.osakkaat, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
    applyEach(polku.osakkaat, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      required(rivi.osakkeita)
    })
  })

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  constructor(
    private _asiakasService: AsiakasService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _lemonatorFirebase: FirebaseLemonator,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _kycUriService: KycUriService,
    private _dateService: DateService,
    private _validationService: FormValidationService,
    private _snackbar: MatSnackBar,
    private _changeDetectionRef: ChangeDetectorRef,
    private _veroilmoitusLaskentaService: VeroilmoitusLaskentaService,
    private _timestampService: TimestampService,
    private _stringService: StringService,
  ) {}

  ngOnInit(): void {
    const veroilmoOsakkaatObservable: Observable<VeroilmoitusOsakas[]> = this.veroilmoitusObservable.pipe(map(veroilmoitus => veroilmoitus?.osakkaat ?? []))

    const tuntemistiedotObservable: Observable<KnowYourCustomer> = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avaintiedot => {
        if (!avaintiedot) {
          return observableOf(null)
        }
        return this._lemonaidFirebase.firestoreDoc<KnowYourCustomer>(this._kycUriService.getCustomerKycDocUri(avaintiedot.avain)).listen()
      }),
    )

    const combinedObservable: Observable<VeroilmoitusOsakas[]> = veroilmoOsakkaatObservable.pipe(
      switchMap(veroilmoitusOsakkaat => {
        if (veroilmoitusOsakkaat?.length) {
          this.isDataFromKyc.set(false)
          this.tuntemistiedotTallennettu.set('')
          return observableOf<VeroilmoitusOsakas[]>(veroilmoitusOsakkaat)
        }
        return tuntemistiedotObservable.pipe(
          tap(kyc => {
            this.isDataFromKyc.set(!!kyc)
            this.tuntemistiedotTallennettu.set(kyc ? this._dateService.muotoilePaivaJaAikaDate(kyc.paivitetty.toDate(), 'fi') : '')
          }),
          map(kyc => {
            return kyc ? kyc.osakkaat.map(osakas => this._veroilmoitusLaskentaService.convertKycToVeroilmoitusOsakas(osakas)) : []
          }),
        )
      }),
    )

    combinedObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(osakkaat => {
      this._cache = []
      this._rivienTilaukset.forEach(tilaus => tilaus.unsubscribe())
      this._rivienTilaukset = []
      asetaArvo(this.lomake.osakkaat, [], { ilmoita: false })

      for (const osakas of osakkaat) {
        this.lisaaOsakas(osakas)
      }

      this._changeDetectionRef.markForCheck()
    })
  }

  async tallenna() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return 'failed'
    }

    const data: VeroilmoitusOsakas[] = [...this._cache]
    const [avaintiedot, veroilmoitus, kirjanpitajanTiedot] = await Promise.all([
      firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable),
      firstValueFrom(this.veroilmoitusObservable),
      this._kirjautunutKayttajaService.getKirjanpitajanTiedot(),
    ])

    const tilikausiAvain = veroilmoitus?.perustiedot?.tilikausi?.avain
    if (!avaintiedot || !tilikausiAvain || !kirjanpitajanTiedot) {
      throw new Error('Avaimet puuttuvat')
    }

    const veroilmoitusUri = this._kirjanpitoUriService.annaVeroilmoituksenDraftUri(avaintiedot.avain, veroilmoitus.perustiedot.tilikausi)
    const historiaAvain = this._lemonatorFirebase.firestoreCreateId()
    const osakkaatHistoriaUri = this._kirjanpitoUriService.annaVeroilmoituksenOsakkaatHistoriaUri(avaintiedot.avain, tilikausiAvain, historiaAvain)

    const veroilmoitusUpdateData: Pick<VeroilmoitusDraft, 'osakkaat'> = {
      osakkaat: data,
    }
    const historiaData: VeroilmoitusOsakkaatHistoria = {
      osakkaat: data,
      muokattu: this._timestampService.now(),
      muokkaaja: kirjanpitajanTiedot.uid,
    }
    const batch = this._lemonatorFirebase.firestoreBatch()
    batch.set(veroilmoitusUri, veroilmoitusUpdateData, { merge: true })
    batch.set(osakkaatHistoriaUri, historiaData)
    await batch.commit()

    if (this.showSnackbar) {
      this._snackbar.open('Osakkaiden tiedot lisätty veroilmoitukseen', 'OK', { duration: 3000 })
    }
    this.isDataFromKyc.set(false)
    return 'success'
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  lisaaOsakas(firestoresta?: VeroilmoitusOsakas) {
    const osakas: VeroilmoitusOsakas = {
      nimi: firestoresta?.nimi || null,
      hetuTaiYtunnus: firestoresta?.hetuTaiYtunnus || null,
      osakkeita: firestoresta?.osakkeita || null,
    }

    // Rivin lisäys ja poisto eivät reaktiivisessa lomakkeessa ilmoittaneet muiden rivien kenttien tilaajille
    const rivit = this.lomake.osakkaat().value()
    asetaArvo(this.lomake.osakkaat, [...rivit, { nimi: osakas.nimi ?? '', hetu: osakas.hetuTaiYtunnus ?? '', osakkeita: osakas.osakkeita }], { ilmoita: false })
    const rivi = this.lomake.osakkaat[rivit.length]

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })
    const tilaukset = new Subscription()
    tilaukset.add(
      muutokset(rivi.nimi)
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(value => {
          osakas.nimi = value || null
        }),
    )
    tilaukset.add(
      muutokset(rivi.hetu)
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(value => {
          osakas.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
        }),
    )
    tilaukset.add(
      muutokset(rivi.osakkeita)
        .pipe(takeUntil(this._ngUnsubscribe))
        .subscribe(value => {
          osakas.osakkeita = value || null
        }),
    )

    this._rivienTilaukset.push(tilaukset)
    this._cache.push(osakas)
  }

  poistaOsakas(index: number) {
    asetaArvo(
      this.lomake.osakkaat,
      this.lomake
        .osakkaat()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._rivienTilaukset[index].unsubscribe()
    this._rivienTilaukset.splice(index, 1)
    this._cache.splice(index, 1)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
